import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Clock, Sparkles, Award } from "lucide-react";
import BookingWidget from "../components/BookingWidget";

const VEHICLE_PREVIEW = [
  {
    name: "Sedan",
    sub: "Mercedes-Benz E-Class",
    img: "https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/3f0b448cde6206bf0667628635dc0a786a21db8e92080f7515faccd4efa5dd51.png",
    seats: "3 Passengers · 2 Bags",
    price: "$85 / hr",
  },
  {
    name: "SUV",
    sub: "Cadillac Escalade",
    img: "https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/a248946b1a97ba4db53834de226b08e879fc3f0be6f9dbc2fad3d937e6f60748.png",
    seats: "6 Passengers · 5 Bags",
    price: "$125 / hr",
  },
];

const TESTIMONIALS = [
  {
    name: "Eleanor Voss",
    role: "Managing Partner, Klein & Voss LLP",
    avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3Nzk3MzEwOTh8MA&ixlib=rb-4.1.0&q=85",
    quote: "I've used Black Sapphire for three years. The car is always early, always immaculate, always silent. That's all I need.",
  },
  {
    name: "Marcus Antoine",
    role: "CTO, Helix Capital",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3Nzk3MzEwOTh8MA&ixlib=rb-4.1.0&q=85",
    quote: "When my flight got rerouted at 2am, the chauffeur was already waiting at the new terminal. They simply knew.",
  },
  {
    name: "Sofia Marchetti",
    role: "Creative Director, Marchetti Studio",
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3Nzk3MzEwOTh8MA&ixlib=rb-4.1.0&q=85",
    quote: "Booked them for a 9-city European tour. Same precision in Milan as in Munich. Worth every cent.",
  },
];

export default function Landing() {
  return (
    <div data-testid="landing-page" className="bg-[#0A0A0A]">
      {/* HERO */}
      <section className="relative min-h-[100vh] overflow-hidden grain">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/e228dd255c65ab57430241e7787c29f32cf4a55f8f5b378e8411556765de7c6c.png')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/60" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-32 pb-20 lg:pt-40 lg:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 xl:col-span-7">
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#C5A059] fade-up">
              Est. 1998 · Private Chauffeur Service
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-6 leading-[0.95] text-white fade-up delay-1">
              The journey is<br/>
              <span className="italic font-light text-white/90">part of the </span>
              <span className="italic text-[#C5A059]">arrival.</span>
            </h1>
            <p className="text-[#A3A3A3] text-base sm:text-lg mt-8 max-w-xl leading-relaxed fade-up delay-2">
              A discreet fleet of black sedans, SUVs and limousines, driven by professionally vetted
              chauffeurs. Reservations confirmed in under a minute, available in 40+ cities worldwide.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3 fade-up delay-3">
              <Link
                to="/book"
                data-testid="hero-reserve-cta"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#0A0A0A] hover:bg-[#C5A059] hover:text-white transition-colors duration-300 px-8 py-4 text-xs uppercase tracking-[0.3em]"
              >
                Reserve a Car <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="#fleet"
                data-testid="hero-fleet-cta"
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white px-8 py-4 text-xs uppercase tracking-[0.3em] transition-colors duration-300"
              >
                View Our Fleet
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-xl fade-up delay-4">
              {[
                { k: "40+", v: "Cities" },
                { k: "150k", v: "Rides /yr" },
                { k: "4.97", v: "Avg Rating" },
              ].map((s) => (
                <div key={s.v} className="border-l border-white/10 pl-4">
                  <div className="font-heading text-3xl text-white">{s.k}</div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 xl:col-span-5 fade-up delay-2">
            <BookingWidget />
          </div>
        </div>
      </section>

      {/* WHY BLACK SAPPHIRE */}
      <section className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-24">
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#C5A059]">The Sapphire Standard</div>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4 text-white max-w-3xl leading-[1.05]">
          Service that arrives before <span className="italic text-[#C5A059]">you ask</span>.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-16">
          <div className="md:col-span-7 border border-white/10 p-10 relative overflow-hidden min-h-[380px] flex flex-col justify-end" data-testid="feature-highlight-1">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/a2d6565e3e8b4f9f9df514630f073661d789f736bb6f5c2d70ea3d17b81f9aa7.png"
              alt="Chauffeur"
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
            <div className="relative">
              <ShieldCheck className="w-8 h-8 text-[#C5A059]" />
              <h3 className="font-heading text-2xl text-white mt-4">Vetted Chauffeurs, Discreet by Design</h3>
              <p className="text-[#A3A3A3] mt-3 max-w-md">Every driver passes a 14-step background check, defensive driving certification and confidentiality training.</p>
            </div>
          </div>

          <div className="md:col-span-5 grid grid-cols-1 gap-6">
            <div className="border border-white/10 p-8" data-testid="feature-card-2">
              <Clock className="w-7 h-7 text-[#C5A059]" />
              <h3 className="font-heading text-xl text-white mt-3">On Time, Every Time</h3>
              <p className="text-[#A3A3A3] text-sm mt-2 leading-relaxed">Flight-tracked airport pickups with 60 minutes complimentary wait time.</p>
            </div>
            <div className="border border-white/10 p-8" data-testid="feature-card-3">
              <Sparkles className="w-7 h-7 text-[#C5A059]" />
              <h3 className="font-heading text-xl text-white mt-3">Immaculate Interiors</h3>
              <p className="text-[#A3A3A3] text-sm mt-2 leading-relaxed">Every vehicle is detailed before each ride. Bottled water, Wi-Fi and phone chargers included.</p>
            </div>
          </div>

          <div className="md:col-span-5 border border-white/10 p-8" data-testid="feature-card-4">
            <Award className="w-7 h-7 text-[#C5A059]" />
            <h3 className="font-heading text-xl text-white mt-3">Member-Grade Pricing</h3>
            <p className="text-[#A3A3A3] text-sm mt-2 leading-relaxed">Transparent quotes, no surge pricing, no last-minute fees. The price you see is what you pay.</p>
          </div>

          <div className="md:col-span-7 border border-white/10 p-10 relative overflow-hidden min-h-[260px]" data-testid="feature-highlight-2">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/ff457d5d-eff6-4efa-b91c-eab4bd8c5284/images/ede70952f527d1c2b0619649d71b77cfb6d6dda8def8cfb82194ea7dfd62fa8f.png"
              alt="Interior"
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/70 to-transparent" />
            <div className="relative max-w-md">
              <h3 className="font-heading text-2xl text-white">A Cabin That Feels Like an Embassy</h3>
              <p className="text-[#A3A3A3] mt-3 text-sm leading-relaxed">Climate-zoned rear seating, premium leather, ambient lighting and a silent ride. Conduct business or simply exhale.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FLEET */}
      <section id="fleet" className="bg-[#0A0A0A] py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#C5A059]">The Fleet</div>
              <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4 text-white leading-[1.05]">
                Two classes. <span className="italic">One standard</span>.
              </h2>
            </div>
            <Link
              to="/book"
              data-testid="fleet-reserve-link"
              className="text-xs uppercase tracking-[0.3em] text-white border-b border-[#C5A059] pb-1 hover:text-[#C5A059] transition-colors self-start"
            >
              Compare & Reserve →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VEHICLE_PREVIEW.map((v) => (
              <div
                key={v.name}
                data-testid={`fleet-card-${v.name.toLowerCase()}`}
                className="bg-[#141414] border border-white/5 hover:border-[#C5A059]/50 hover:-translate-y-1 transition-all duration-500 group grid grid-cols-1 sm:grid-cols-5"
              >
                <div className="sm:col-span-3 bg-[#0A0A0A] aspect-[4/3] sm:aspect-auto overflow-hidden">
                  <img src={v.img} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="sm:col-span-2 p-7 flex flex-col justify-center">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{v.name}</div>
                  <h3 className="font-heading text-2xl text-white mt-2">{v.sub}</h3>
                  <p className="text-xs text-white/50 mt-2 uppercase tracking-wider">{v.seats}</p>
                  <div className="mt-5 pt-5 border-t border-white/10">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Hourly Rate</div>
                    <div className="font-heading text-xl text-white mt-1">{v.price}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Hourly pricing strip */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <div className="border border-white/10 p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Sedan · Hourly Service</div>
              <ul className="mt-4 space-y-2 text-sm text-white/80">
                <li className="flex justify-between"><span>Hourly rate</span><span className="text-white">$85.00 / hr</span></li>
                <li className="flex justify-between"><span>Minimum booking</span><span className="text-white">2 hours</span></li>
                <li className="flex justify-between"><span>Free wait time</span><span className="text-white">15 minutes</span></li>
              </ul>
            </div>
            <div className="border border-white/10 p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">SUV · Hourly Service</div>
              <ul className="mt-4 space-y-2 text-sm text-white/80">
                <li className="flex justify-between"><span>Hourly rate</span><span className="text-white">$125.00 / hr</span></li>
                <li className="flex justify-between"><span>Minimum booking</span><span className="text-white">2 hours</span></li>
                <li className="flex justify-between"><span>Free wait time</span><span className="text-white">15 minutes</span></li>
              </ul>
            </div>
          </div>

          <div className="border border-[#C5A059]/30 mt-6 p-6 bg-[#C5A059]/5 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059]">Every Ride Includes</div>
              <p className="text-white/90 mt-2 text-sm">Professional chauffeur · Bottled water · 15 min free wait time · Tolls within service area</p>
            </div>
            <Link
              to="/book"
              className="text-xs uppercase tracking-[0.3em] text-white border-b border-[#C5A059] pb-1 hover:text-[#C5A059] transition-colors whitespace-nowrap"
            >
              Book & Reserve →
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-24">
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#C5A059]">From the back seat</div>
        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl mt-4 text-white max-w-3xl leading-[1.05]">
          Quiet words from <span className="italic">loud rooms</span>.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} data-testid={`testimonial-${i}`} className="border border-white/10 p-8 flex flex-col">
              <div className="text-5xl font-heading text-[#C5A059] leading-none">“</div>
              <p className="text-white/85 mt-4 leading-relaxed flex-1">{t.quote}</p>
              <div className="flex items-center gap-3 mt-8 pt-6 border-t border-white/10">
                <img src={t.avatar} alt={t.name} className="w-10 h-10 object-cover rounded-full grayscale" />
                <div>
                  <div className="text-white text-sm">{t.name}</div>
                  <div className="text-white/50 text-xs uppercase tracking-wider">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pb-24">
        <div className="border border-white/10 p-12 md:p-20 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-[#C5A059]/10 blur-3xl rounded-full" />
          <div className="relative">
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              Ride <span className="italic text-[#C5A059]">like you mean it</span>.
            </h2>
            <p className="text-[#A3A3A3] mt-6 max-w-xl mx-auto">Reserve in 60 seconds. Confirmation arrives instantly. Your chauffeur arrives ten minutes early.</p>
            <Link
              to="/book"
              data-testid="footer-cta-reserve"
              className="inline-flex items-center justify-center gap-2 mt-10 bg-white text-[#0A0A0A] hover:bg-[#C5A059] hover:text-white transition-colors duration-300 px-10 py-4 text-xs uppercase tracking-[0.3em]"
            >
              Reserve Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
